import { EntityType } from '../../objects/interface-library';

/**
 * The entity-type ↔ attachment linkage rules, in one place.
 *
 * An Attachment is stored flat with `associatedEntityId` + `associatedEntityType`,
 * but the *source* of that id differs per entity type — a Contract carries
 * `contractId`, a Party carries `vendorNum`, a Person carries `contactNum`, and
 * so on. That mapping used to live inline in NewAttachmentComponent's save
 * switch; it lives here so every dialog that creates an attachment resolves the
 * id identically and the two can't drift apart.
 *
 * Only six entity types can hold attachments. Anything else must be rejected
 * rather than saved with a blank id — an attachment with no resolvable parent is
 * orphaned and unreachable from every UI that lists attachments by entity.
 */

/** The entity types that can be an attachment's parent. */
export const ATTACHABLE_ENTITY_TYPES: readonly EntityType[] = [
  EntityType.Contract,
  EntityType.Party,
  EntityType.Baa,
  EntityType.Coi,
  EntityType.Person,
  EntityType.User,
] as const;

/** Human labels for the six attachable types (picker chips, headers, messages). */
export const ATTACHABLE_TYPE_LABELS: Record<string, string> = {
  [EntityType.Contract]: 'Contract',
  [EntityType.Party]: 'Party / Vendor',
  [EntityType.Baa]: 'BAA',
  [EntityType.Coi]: 'COI',
  [EntityType.Person]: 'Person',
  [EntityType.User]: 'User',
};

export function isAttachableEntityType(entityType: any): boolean {
  return ATTACHABLE_ENTITY_TYPES.includes(entityType);
}

/**
 * The id an attachment should be filed under for this entity.
 *
 * Returns null for an unrecognized type OR a recognized type whose id field is
 * empty — callers must treat both as "cannot attach" rather than saving an
 * orphan. Mirrors the original switch in NewAttachmentComponent exactly.
 */
export function resolveAttachmentEntityId(entity: any): string | null {
  if (!entity) return null;

  let id: any = null;
  switch (entity.entityType) {
    case EntityType.Party:
      id = entity.vendorNum;
      break;
    case EntityType.Contract:
      id = entity.contractId;
      break;
    case EntityType.Coi:
      id = entity.coiId;
      break;
    case EntityType.Baa:
      id = entity.baaId;
      break;
    case EntityType.Person:
      id = entity.contactNum;
      break;
    case EntityType.User:
      id = entity.userNum;
      break;
    default:
      return null;
  }

  // An empty id is as unusable as an unknown type — don't orphan the attachment.
  return id ? String(id) : null;
}

/** Display label stored on the attachment so the attachments grid can show its parent. */
export function getAttachmentEntityDisplayName(entity: any): string {
  switch (entity?.entityType) {
    case EntityType.Contract:
      return entity.nameOfContract || entity.friendlyName || entity.contractId || '';
    case EntityType.Party:
      return entity.name || entity.vendorNum || '';
    case EntityType.Baa:
      return entity.name || entity.baaId || '';
    case EntityType.Coi:
      return entity.name || entity.coiId || '';
    case EntityType.Person:
      return entity.friendlyName || entity.fullName || entity.contactNum || '';
    case EntityType.User:
      return entity.friendlyName || entity.email || entity.userNum || '';
    default:
      return '';
  }
}

/**
 * Stamp the parent-entity fields onto an attachment-shaped object.
 *
 * Returns false (leaving the object untouched) when the entity can't host an
 * attachment, so callers can surface an error instead of saving an orphan.
 * `contractFriendlyName` is set only for contracts — the attachments grid has a
 * dedicated column for it.
 */
export function applyAttachmentTarget(attachment: any, entity: any): boolean {
  const entityId = resolveAttachmentEntityId(entity);
  if (!entityId) return false;

  const displayName = getAttachmentEntityDisplayName(entity);
  attachment.associatedEntityId = entityId;
  attachment.associatedEntityType = entity.entityType;
  attachment.associatedEntityViewValue = displayName;
  if (entity.entityType === EntityType.Contract) {
    attachment.contractFriendlyName = displayName;
  }
  return true;
}
